// Advanced Search Modal Component - ระบบค้นหาและกรองขั้นสูง
import React, { useState, useEffect } from 'react';
import Modal from '../ui/Modal';
import ValidatedInput from '../ui/ValidatedInput';
import { 
  RiSearchLine, 
  RiFilterLine, 
  RiSaveLine,
  RiDeleteBinLine,
  RiStarLine,
  RiStarFill,
  RiRefreshLine,
  RiCloseLine
} from 'react-icons/ri';

export interface SearchFilter {
  id?: string;
  name: string;
  description?: string;
  filters: Record<string, any>;
  isDefault?: boolean;
  isFavorite?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface SearchField {
  key: string;
  label: string;
  type: 'text' | 'select' | 'date' | 'dateRange' | 'number' | 'boolean';
  options?: Array<{ value: string; label: string }>;
  placeholder?: string;
  validation?: {
    required?: boolean;
    min?: number;
    max?: number;
    pattern?: RegExp;
  };
}

export interface AdvancedSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSearch: (filters: Record<string, any>) => void;
  onSaveFilter: (filter: Omit<SearchFilter, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  onDeleteFilter: (filterId: string) => Promise<void>;
  searchFields: SearchField[];
  savedFilters: SearchFilter[];
  currentFilters: Record<string, any>;
  title?: string;
  loading?: boolean;
}

const AdvancedSearchModal: React.FC<AdvancedSearchModalProps> = ({
  isOpen,
  onClose,
  onSearch,
  onSaveFilter,
  onDeleteFilter,
  searchFields,
  savedFilters,
  currentFilters,
  title = 'ค้นหาขั้นสูง',
  loading = false
}) => {
  const [filters, setFilters] = useState<Record<string, any>>(currentFilters);
  const [showSaveDialog, setShowSaveDialog] = useState<boolean>(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [filterToDelete, setFilterToDelete] = useState<SearchFilter | null>(null);
  const [filterName, setFilterName] = useState<string>('');
  const [filterDescription, setFilterDescription] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'search' | 'saved'>('search');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setFilters(currentFilters);
  }, [currentFilters]);

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    
    // Clear error for this field
    if (errors[key]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      });
    }
  };

  const validateFilters = () => {
    const newErrors: Record<string, string> = {};

    searchFields.forEach(field => {
      const value = filters[field.key];
      const validation = field.validation;

      if (validation?.required && (!value || (typeof value === 'string' && value.trim() === ''))) {
        newErrors[field.key] = `${field.label}เป็นข้อมูลที่จำเป็น`;
      }

      if (field.type === 'number' && value !== undefined && value !== '') {
        const numValue = Number(value);
        if (isNaN(numValue)) {
          newErrors[field.key] = `${field.label}ต้องเป็นตัวเลข`;
        } else {
          if (validation?.min !== undefined && numValue < validation.min) {
            newErrors[field.key] = `${field.label}ต้องไม่น้อยกว่า ${validation.min}`;
          }
          if (validation?.max !== undefined && numValue > validation.max) {
            newErrors[field.key] = `${field.label}ต้องไม่มากกว่า ${validation.max}`;
          }
        }
      }

      if (field.type === 'dateRange' && value) {
        const { from, to } = value;
        if (from && to && new Date(from) > new Date(to)) {
          newErrors[field.key] = 'วันที่เริ่มต้นต้องไม่เกินวันที่สิ้นสุด';
        }
      }

      if (validation?.pattern && value && !validation.pattern.test(value)) {
        newErrors[field.key] = `รูปแบบ${field.label}ไม่ถูกต้อง`;
      }
    });

    setErrors(newErrors);

    // Scroll to first error if validation fails
    if (Object.keys(newErrors).length > 0) {
      const firstErrorField = Object.keys(newErrors)[0];
      const errorElement = document.querySelector(`[name="${firstErrorField}"], [name="${firstErrorField}_from"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        (errorElement as HTMLInputElement).focus();
      }
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleSearch = () => {
    if (validateFilters()) {
      // Remove empty values
      const cleanedFilters = Object.keys(filters).reduce((acc, key) => {
        const value = filters[key];
        if (value !== undefined && value !== '' && value !== null) {
          acc[key] = value;
        }
        return acc;
      }, {} as Record<string, any>);

      onSearch(cleanedFilters);
      onClose();
    }
  };

  const handleClearFilters = () => {
    setFilters({});
    setErrors({});
  };

  const handleLoadSavedFilter = (savedFilter: SearchFilter) => {
    setFilters(savedFilter.filters);
    setErrors({});
    setActiveTab('search');
  };

  const handleSaveCurrentFilter = async () => {
    if (!filterName.trim()) {
      setErrors({ filterName: 'กรุณาระบุชื่อตัวกรอง' });
      return;
    }

    try {
      await onSaveFilter({
        name: filterName.trim(),
        description: filterDescription.trim(),
        filters: filters,
        isFavorite: false
      });

      setShowSaveDialog(false);
      setFilterName('');
      setFilterDescription('');
      setErrors({});
    } catch (error) {
      console.error('Error saving filter:', error);
      setErrors({ save: 'ไม่สามารถบันทึกตัวกรองได้' });
    }
  };

  const handleDeleteSavedFilter = (filter: SearchFilter) => {
    setFilterToDelete(filter);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteFilter = async () => {
    if (!filterToDelete?.id) return;

    try {
      await onDeleteFilter(filterToDelete.id);
      setShowDeleteConfirm(false);
      setFilterToDelete(null);
    } catch (error) {
      console.error('Error deleting filter:', error);
    }
  };

  const renderSearchField = (field: SearchField) => {
    const value = filters[field.key] || '';
    const error = errors[field.key];

    switch (field.type) {
      case 'text':
        return (
          <ValidatedInput
            key={field.key}
            label={field.label}
            name={field.key}
            value={value}
            onChange={(e) => handleFilterChange(field.key, e.target.value)}
            error={error}
            placeholder={field.placeholder}
            disabled={loading}
          />
        );

      case 'select':
        return (
          <div key={field.key}>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              {field.label}
            </label>
            <select
              // KD3 S2495: ป้ายชื่อไม่ได้ผูกกับช่อง → screen reader อ่านชื่อ select ไม่ได้ (เหลือแค่ค่า "ทั้งหมด")
              aria-label={field.label}
              name={field.key}
              value={value}
              onChange={(e) => handleFilterChange(field.key, e.target.value)}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${
                error ? 'border-danger-300' : 'border-secondary-300'
              }`}
              disabled={loading}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? `${field.key}-error` : undefined}
            >
              <option value="">ทั้งหมด</option>
              {field.options?.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {error && (
              <p id={`${field.key}-error`} className="mt-1 text-sm text-danger-600">{error}</p>
            )}
          </div>
        );

      case 'date':
        return (
          <div key={field.key} className="space-y-2">
            <label className="block text-sm font-medium text-secondary-700">
              {field.label}
            </label>
            <input
              type="date"
              aria-label={field.label}
              name={field.key}
              value={value}
              onChange={(e) => handleFilterChange(field.key, e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                error ? 'border-danger-500' : 'border-secondary-300'
              } ${loading ? 'bg-secondary-100' : ''}`}
              disabled={loading}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? `${field.key}-error` : undefined}
            />
            {error && (
              <p id={`${field.key}-error`} className="text-sm text-danger-500">{error}</p>
            )}
          </div>
        );

      case 'dateRange':
        return (
          <div key={field.key}>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              {field.label}
            </label>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="date"
                aria-label={`${field.label} (เริ่มต้น)`}
                name={`${field.key}_from`}
                value={value?.from || ''}
                onChange={(e) => handleFilterChange(field.key, { ...value, from: e.target.value })}
                className={`px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${
                  error ? 'border-danger-300' : 'border-secondary-300'
                }`}
                placeholder="เริ่มต้น"
                disabled={loading}
                aria-invalid={Boolean(error)}
                aria-describedby={error ? `${field.key}-error` : undefined}
              />
              <input
                type="date"
                aria-label={`${field.label} (สิ้นสุด)`}
                name={`${field.key}_to`}
                value={value?.to || ''}
                onChange={(e) => handleFilterChange(field.key, { ...value, to: e.target.value })}
                className={`px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${
                  error ? 'border-danger-300' : 'border-secondary-300'
                }`}
                placeholder="สิ้นสุด"
                disabled={loading}
                aria-invalid={Boolean(error)}
                aria-describedby={error ? `${field.key}-error` : undefined}
              />
            </div>
            {error && (
              <p id={`${field.key}-error`} className="mt-1 text-sm text-danger-600">{error}</p>
            )}
          </div>
        );

      case 'number':
        return (
          <ValidatedInput
            key={field.key}
            label={field.label}
            name={field.key}
            type="number"
            value={value}
            onChange={(e) => handleFilterChange(field.key, e.target.value)}
            error={error}
            placeholder={field.placeholder}
            min={field.validation?.min}
            max={field.validation?.max}
            disabled={loading}
          />
        );

      case 'boolean':
        return (
          <div key={field.key}>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              {field.label}
            </label>
            <select
              // KD3 S2495: ป้ายชื่อไม่ได้ผูกกับช่อง → screen reader อ่านชื่อ select ไม่ได้ (เหลือแค่ค่า "ทั้งหมด")
              aria-label={field.label}
              name={field.key}
              value={value === undefined ? '' : value.toString()}
              onChange={(e) => handleFilterChange(field.key, e.target.value === '' ? undefined : e.target.value === 'true')}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${
                error ? 'border-danger-300' : 'border-secondary-300'
              }`}
              disabled={loading}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? `${field.key}-error` : undefined}
            >
              <option value="">ทั้งหมด</option>
              <option value="true">ใช่</option>
              <option value="false">ไม่ใช่</option>
            </select>
            {error && (
              <p id={`${field.key}-error`} className="mt-1 text-sm text-danger-600">{error}</p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const renderSavedFilters = () => {
    if (savedFilters.length === 0) {
      return (
        <div className="text-center py-8">
          <RiFilterLine className="mx-auto h-12 w-12 text-secondary-400" />
          <h3 className="mt-2 text-sm font-medium text-secondary-900">ไม่มีตัวกรองที่บันทึกไว้</h3>
          <p className="mt-1 text-sm text-secondary-500">
            สร้างตัวกรองใหม่และบันทึกไว้เพื่อใช้งานในอนาคต
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {savedFilters.map(savedFilter => (
          <div
            key={savedFilter.id}
            className="flex items-center justify-between p-3 border border-secondary-200 rounded-lg hover:bg-secondary-50"
          >
            <div className="flex-1">
              <div className="flex items-center">
                <h4 className="text-sm font-medium text-secondary-900">{savedFilter.name}</h4>
                {savedFilter.isFavorite && (
                  <RiStarFill className="w-4 h-4 text-warning-400 ml-1" />
                )}
                {savedFilter.isDefault && (
                  <span className="ml-2 px-2 py-1 text-xs bg-primary-100 text-primary-800 rounded">
                    ค่าเริ่มต้น
                  </span>
                )}
              </div>
              {savedFilter.description && (
                <p className="text-xs text-secondary-500 mt-1">{savedFilter.description}</p>
              )}
              <p className="text-xs text-secondary-400 mt-1">
                {Object.keys(savedFilter.filters).length} เงื่อนไข
              </p>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => handleLoadSavedFilter(savedFilter)}
                className="p-1 text-secondary-400 hover:text-primary-600 rounded"
                title="ใช้ตัวกรองนี้"
              >
                <RiSearchLine className="w-4 h-4" />
              </button>
              {savedFilter.id && (
                <button
                  onClick={() => handleDeleteSavedFilter(savedFilter)}
                  className="p-1 text-secondary-400 hover:text-danger-600 rounded"
                  title="ลบตัวกรอง"
                >
                  <RiDeleteBinLine className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const hasActiveFilters = Object.keys(filters).some(key => 
    filters[key] !== undefined && filters[key] !== '' && filters[key] !== null
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        size="xl"
        footer={
          <div className="flex justify-between w-full">
            <div className="flex space-x-2">
              {activeTab === 'search' && hasActiveFilters && (
                <>
                  <button
                    type="button"
                    onClick={() => setShowSaveDialog(true)}
                    className="inline-flex items-center px-3 min-h-[44px] sm:min-h-[38px] py-2 border border-secondary-300 shadow-sm text-sm font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <RiSaveLine className="w-4 h-4 mr-1" />
                    บันทึกตัวกรอง
                  </button>
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    className="inline-flex items-center px-3 min-h-[44px] sm:min-h-[38px] py-2 border border-secondary-300 shadow-sm text-sm font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <RiRefreshLine className="w-4 h-4 mr-1" />
                    ล้างตัวกรอง
                  </button>
                </>
              )}
            </div>
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-md hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                ยกเลิก
              </button>
              {activeTab === 'search' && (
                <button
                  type="button"
                  onClick={handleSearch}
                  disabled={loading}
                  className="inline-flex items-center px-4 min-h-[44px] sm:min-h-[38px] py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  <RiSearchLine className="w-4 h-4 mr-2" />
                  {loading ? 'กำลังค้นหา...' : 'ค้นหา'}
                </button>
              )}
            </div>
          </div>
        }
      >
        <div className="space-y-6">
          {/* Tabs */}
          <div className="border-b border-secondary-200">
            <nav className="-mb-px flex space-x-8" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'search'}
                onClick={() => setActiveTab('search')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'search'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
                }`}
              >
                <RiSearchLine className="w-4 h-4 inline mr-1" />
                ค้นหา
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'saved'}
                onClick={() => setActiveTab('saved')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'saved'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
                }`}
              >
                <RiFilterLine className="w-4 h-4 inline mr-1" />
                ตัวกรองที่บันทึกไว้ ({savedFilters.length})
              </button>
            </nav>
          </div>

          {/* Content */}
          {activeTab === 'search' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
              {searchFields.map(field => renderSearchField(field))}
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              {renderSavedFilters()}
            </div>
          )}

          {/* Error message */}
          {errors.save && (
            <div className="bg-danger-50 border border-danger-200 rounded-md p-4">
              <p className="text-sm text-danger-700">{errors.save}</p>
            </div>
          )}
        </div>
      </Modal>

      {/* Save Filter Dialog */}
      <Modal
        isOpen={showSaveDialog}
        onClose={() => setShowSaveDialog(false)}
        title="บันทึกตัวกรอง"
        size="md"
        footer={
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={() => setShowSaveDialog(false)}
              className="px-4 py-2 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-md hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              ยกเลิก
            </button>
            <button
              type="button"
              onClick={handleSaveCurrentFilter}
              disabled={!filterName.trim() || loading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              บันทึก
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <ValidatedInput
            label="ชื่อตัวกรอง"
            name="filterName"
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            error={errors.filterName}
            placeholder="ตั้งชื่อตัวกรองนี้"
            required
            maxLength={50}
          />
          
          <ValidatedInput
            label="คำอธิบาย (ไม่บังคับ)"
            name="filterDescription"
            value={filterDescription}
            onChange={(e) => setFilterDescription(e.target.value)}
            placeholder="อธิบายการใช้งานตัวกรองนี้"
            maxLength={100}
          />

          <div className="bg-secondary-50 rounded-lg p-3">
            <h4 className="text-sm font-medium text-secondary-900 mb-2">เงื่อนไขที่จะบันทึก:</h4>
            <div className="text-sm text-secondary-600">
              {Object.keys(filters).length > 0 ? (
                <ul className="space-y-1">
                  {Object.entries(filters).map(([key, value]) => {
                    const field = searchFields.find(f => f.key === key);
                    return (
                      <li key={key}>
                        <strong>{field?.label || key}:</strong> {
                          typeof value === 'object' && value !== null 
                            ? `${value.from || ''} - ${value.to || ''}`
                            : value?.toString() || ''
                        }
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p>ไม่มีเงื่อนไขการค้นหา</p>
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* Delete Confirm Dialog */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setFilterToDelete(null);
        }}
        title="ยืนยันการลบตัวกรอง"
        size="sm"
        footer={
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={() => {
                setShowDeleteConfirm(false);
                setFilterToDelete(null);
              }}
              className="px-4 py-2 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-md hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              ยกเลิก
            </button>
            <button
              type="button"
              onClick={confirmDeleteFilter}
              className="px-4 py-2 text-sm font-medium text-white bg-danger-600 border border-transparent rounded-md hover:bg-danger-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-danger-500"
            >
              <RiDeleteBinLine className="w-4 h-4 inline mr-1" />
              ลบตัวกรอง
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-secondary-700">
            คุณต้องการลบตัวกรอง <strong className="text-secondary-900">"{filterToDelete?.name}"</strong> หรือไม่?
          </p>
          <p className="text-xs text-secondary-500">
            การดำเนินการนี้ไม่สามารถย้อนกลับได้
          </p>
        </div>
      </Modal>
    </>
  );
};

export default AdvancedSearchModal;